import Chart from "../components/chartV2/chart";
import { ChartType } from "../proto/generated/chart";
import { SuperChartConfiguration_TimeBucket } from "../proto/generated/superchart-page";
import { updateChartConfig } from "../reducer/chart.reducer";
import { StatusLineSettings } from "../components/chartV2/chart.typings";
import { updateChartTimeBucket, updateChartType, updateComparableSecurities } from "../reducer/chart.reducer";
import {
    selectActiveChartId,
    selectChartComparableSecurities,
    selectChartConfig,
    selectChartPanes,
    selectChartPanesConfig,
    selectChartSeries,
    selectChartSeriesGroups,
    selectChartTimeBucket,
    selectChartType,
    selectIndicators,
    selectTimeBuckets
} from '../selectors/chart'
import store from "../store";
import { HomeStateType } from "../reducer/home.reducer";
import { updatePrimarySecurityAsync } from "../reducer/home.thunks";
import { getSecurityAsync, getSecurityKey } from "./security";
import { convertToTimeBucket } from './timeBuckets'
import { ChartAlertSnapshot } from '../components/alerts/alertModal.typings.ts'
import { Security } from '../proto/generated/security'
import { getIndicatorDisplayName } from './indicators.ts'
import { SingleValueData, Time } from 'lightweight-charts'
import IndicatorSeriesGroup from '../components/chartV2/indicatorSeriesGroup.ts'
import { SeriesGroupCategory } from '../pages/home'
import { DrawingDisplayType } from "../components/chartV2/drawingTools/drawing.interface.ts";
import { INLINE_CROSSHAIR_RESET_EVENT } from '../components/chartV2/chartWrapper/InlineCrosshairOverlay'
import { WebSocketResponseMessage_CandleSubscriptionResponse } from "../proto/generated/websocket.ts";
import { Broker_OrderType, Broker_Variety, OrderType, Product, TransactionType, Validity } from "../proto/generated/order.ts";
import type OrdersOnChartTool from "../components/chartV2/drawingTools/ordersOnChartTool/OrdersOnChartTool.tsx";
import { v4 as uuid } from "uuid";
import { ActionType } from "../components/UndoRedoFunctonalities/UndoRedo.context";
import { defaultScalesAndLinesSettings } from './scalesAndLinesDefaults'
import { defaultStatusLineSettings } from './statusLineDefaults'
import { defaultCanvasSettings } from './canvasSettingsDefaults'
import { defaultTradingSettings } from './chart'

type ChartManagerUndoRedoBridge = {
    undo: () => void
    redo: () => void
    getCanUndo: () => boolean
    getCanRedo: () => boolean
    push?: (data: any, title?: string, action?: ActionType) => void
}

class ChartManager {
    charts: Record<string, Chart> = {};
    _publishToWebsocketManager?: (candles: WebSocketResponseMessage_CandleSubscriptionResponse) => void
    private _undoRedoBridge: ChartManagerUndoRedoBridge | null = null

    setUndoRedoBridge(bridge: ChartManagerUndoRedoBridge | null) {
        this._undoRedoBridge = bridge
    }

    undo() {
        this._undoRedoBridge?.undo()
    }

    redo() {
        this._undoRedoBridge?.redo()
    }

    getCanUndo(): boolean {
        return this._undoRedoBridge?.getCanUndo() ?? false
    }

    getCanRedo(): boolean {
        return this._undoRedoBridge?.getCanRedo() ?? false
    }

    /// Native-invoked chart commands (`chartManager.updateTimeBucket`, etc.) bypass component-level
    /// `useUndoRedoContext().push` calls; record history via the bridge so undo/redo cover them.
    private pushHistory(data: any, title: string | undefined, action: ActionType) {
        try {
            this._undoRedoBridge?.push?.(data, title, action)
        } catch (e) {
            console.warn('[ChartManager] pushHistory failed', e)
        }
    }

    setChart(chartId: string, chart: Chart) {
        this.charts[chartId] = chart;
    }

    getChart(chartId: string) {
        return this.charts[chartId]
    }

    updateTimeBucket(bucket: SuperChartConfiguration_TimeBucket) {
        try {
            console.log("updateTimeBucket", bucket)
            const state = store.getState();
            const activeChartId = selectActiveChartId(state)
            const chartTimeBucket = selectChartTimeBucket(activeChartId)(state);
            if (chartTimeBucket.timeSeries !== bucket.timeSeries || chartTimeBucket.timeValue !== bucket.timeValue) {

                store.dispatch(updateChartTimeBucket({ viewKey: activeChartId, timeBucket: bucket }))
                // Match `TimeBuckets.tsx` data shape so `UndoRedo.context.tsx` switch reads `[0].chartId / previousData / currentData`.
                this.pushHistory(
                    [
                        {
                            chartId: activeChartId,
                            currentData: bucket,
                            previousData: chartTimeBucket
                        }
                    ],
                    `Time Bucket (${bucket.name || ''})`,
                    ActionType.TIME_BUCKET
                )
            }
        } catch (e) { }
    }

    updateChartType(chartType: ChartType) {
        try {
            const state = store.getState();
            const activeChartId = selectActiveChartId(state)
            const selectedChartType = selectChartType(activeChartId)(state)

            if (selectedChartType !== chartType) {
                store.dispatch(updateChartType({ viewKey: activeChartId, chartType: chartType }))
                // Match `ChartTypeMenu.tsx` data shape (`chartID`, lowercase ID variant in undo context).
                this.pushHistory(
                    [
                        {
                            chartID: activeChartId,
                            currentData: chartType,
                            previousData: selectedChartType
                        }
                    ],
                    `Chart Type`,
                    ActionType.CHART_TYPE
                )
            }
        } catch (e) { }
    }
    addIndicator(indicatorCode: string) {
        try {
            const state = store.getState();
            const activeChartId = selectActiveChartId(state)
            const chart = this.getChart(activeChartId)
            const indicators = selectIndicators(state);
            const indicator = indicators.find(i => i.code == indicatorCode)
            if (!chart || !indicator) return;
            chart.addIndicator(indicator);
        } catch (e) { }
    }
    getChartSnapshot() {
        try {
            const state = store.getState();
            const activeChartId = selectActiveChartId(state)
            const chart = this.getChart(activeChartId)

            if (!chart) return;
            return chart.getChartSnapshot();
        } catch (e) { }
    }
    getActiveChartId(): string {
        return selectActiveChartId(store.getState())
    }
    /**
     * Effective (defaults merged with per-chart overrides) settings of the active chart, as a
     * JSON string. Native settings UIs call this when opened so they show the values of the
     * chart the user tapped rather than whatever was last edited on another pane.
     */
    getActiveChartSettings(): string {
        try {
            const state = store.getState()
            const activeChartId = selectActiveChartId(state)
            const config = selectChartConfig(activeChartId)(state)
            const scaleOptions = config?.scaleOptions
            const scaleOnLeft = !!scaleOptions?.leftScaleVisible && !scaleOptions?.rightScaleVisible
            const priceScaleMode = (scaleOnLeft ? scaleOptions?.leftScaleMode : scaleOptions?.rightScaleMode) ?? 0
            return JSON.stringify({
                chartId: activeChartId,
                invertScale: config?.scaleInvertOption?.invertScaleMode ?? false,
                scaleOnLeft,
                priceScaleMode,
                scalesAndLines: { ...defaultScalesAndLinesSettings, ...(config?.scalesAndLinesSettings ?? {}) },
                statusLine: { ...defaultStatusLineSettings, ...(config?.statusLineSettings ?? {}) },
                canvas: { ...defaultCanvasSettings, ...(config?.canvasSettings ?? {}) },
                trading: { ...defaultTradingSettings, ...(config?.tradingSettings ?? {}) },
            })
        } catch (e) {
            return '{}'
        }
    }
    /**
     * Forces all chart instances to re-measure their container and repaint.
     * Called from Android native after toggling fullscreen — the WebView's
     * ResizeObserver does not reliably fire when the native view is resized
     * by the Compose layout system, so we explicitly push the new dimensions.
     */
    /**
     * Drops the embedded overview chart's inline-crosshair state (long-press card, two-finger
     * range, native `onOverviewInteraction` lock). Native hosts cache the WebView, so call this
     * when the hosting sheet is dismissed or the WebView is handed to another view.
     */
    resetInlineCrosshair() {
        try {
            window.dispatchEvent(new Event(INLINE_CROSSHAIR_RESET_EVENT))
        } catch (e) { }
    }
    forceResize() {
        try {
            Object.values(this.charts).forEach(chart => {
                if (!chart.chartApi || !chart.container) return
                const w = chart.container.clientWidth
                const h = chart.container.clientHeight
                if (w > 0 && h > 0) {
                    chart.chartApi.applyOptions({ width: w, height: h })
                }
            })
        } catch (e) { }
    }
    updateSelectedDateRange(code: string) {
        try {
            const state = store.getState();
            const chartDurations = state.chartStore.dateRanges || [];

            const selectedDateRange = chartDurations.find(range => range.code === code);
            if (!selectedDateRange) return;

            const activeChartId = selectActiveChartId(state);
            const chart = this.getChart(activeChartId);

            if (!chart) return;

            chart.setSelectedDateRange(selectedDateRange);
        } catch (e) { }
    }
    handleChartSettingsUpdate(settings: any) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state)
        const chart = this.getChart(activeChartId);
        const series = chart?.primarySeriesGroup?.getPrimarySeries();
        if (!series) return;
        series.updateOptions(settings);
    }
    updateStatusLineSetting(settings: StatusLineSettings) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state);
        const chart = this.getChart(activeChartId);
        if (!chart) return;
        if (!settings || typeof settings !== 'object') {
            console.error("Invalid settings passed:", settings);
            return;
        }
        store.dispatch(updateChartConfig({
            viewKey: activeChartId,
            config: { statusLineSettings: settings },
            createIfAbsent: true,
        }));
    }
    updatePriceScaleMode(scaleMode: number) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state)
        const chart = this.getChart(activeChartId);
        chart.updatePriceScaleMode(scaleMode, false)
    }

    /** Toggle invert scale on the active chart. Called from Android native. */
    updateInvertScale(invert: boolean) {
        try {
            const activeChartId = selectActiveChartId(store.getState())
            const chart = this.getChart(activeChartId)
            if (!chart) return
            chart.updateInvertScale(invert)
        } catch (e) { }
    }

    /** Move the primary price scale between left and right. Called from Android native. */
    movePrimaryPriceScale() {
        try {
            const activeChartId = selectActiveChartId(store.getState())
            const chart = this.getChart(activeChartId)
            if (!chart) return
            chart.movePrimaryPriceScale()
        } catch (e) { }
    }

    /**
     * Update canvas settings (grid, crosshair, scale text).
     * Called from Android native.
     */
    updateCanvasSettings(settings: import('../components/chartV2/chart.typings').CanvasSettings) {
        try {
            const activeChartId = selectActiveChartId(store.getState())
            if (!activeChartId) return
            store.dispatch(updateChartConfig({
                viewKey: activeChartId,
                config: { canvasSettings: settings },
                createIfAbsent: true,
            }))
        } catch (e) { }
    }

    /**
     * Update trading settings (buy/sell buttons, orders on chart, alerts on chart).
     * Called from Android native.
     */
    updateTradingSettings(settings: import('../components/chartV2/chart.typings').TradingSettings) {
        try {
            const activeChartId = selectActiveChartId(store.getState())
            if (!activeChartId) return
            store.dispatch(updateChartConfig({
                viewKey: activeChartId,
                config: { tradingSettings: settings },
                createIfAbsent: true,
            }))
        } catch (e) { }
    }
    updateScalesAndLinesSettings(settings: import('../components/chartV2/chart.typings').ScalesAndLinesSettings) {
        try {
            const activeChartId = selectActiveChartId(store.getState())
            if (!activeChartId) return
            store.dispatch(updateChartConfig({
                viewKey: activeChartId,
                config: { scalesAndLinesSettings: settings },
                createIfAbsent: true,
            }))
        } catch (e) { }
    }

    resetChartTimeScale() {
        try {
            const state = store.getState();
            const activeChartId = selectActiveChartId(state)
            const chart = this.getChart(activeChartId);
            if (!chart) return;
            chart.resetTimeScale();
        } catch (e) {
            console.error("Error in reset time scale:", e);
        }
    }
    updatePrimarySecurity(security: HomeStateType["primarySecurity"]) {
        try {
            const prev = store.getState().homeStore.primarySecurity
            const previousData = {
                key: prev?.key,
                expressionMode: prev?.expressionMode,
                securities: prev?.securities,
                underlyingSecurityCode: prev?.underlyingSecurityCode
            }
            const currentData = {
                key: security.key,
                expressionMode: security.expressionMode,
                securities: security.securities,
                underlyingSecurityCode: security.underlyingSecurityCode
            }
            const changed =
                previousData.key !== currentData.key ||
                previousData.expressionMode !== currentData.expressionMode ||
                previousData.underlyingSecurityCode !== currentData.underlyingSecurityCode
            if (changed && previousData.key) {
                // Match `SecuritySearch.tsx` data shape (single-element array of `{ currentData, previousData }`).
                this.pushHistory(
                    [{ currentData, previousData }],
                    `Symbol changed`,
                    ActionType.SECURITY_CHANGE
                )
            }
        } catch (e) {
            console.warn('[ChartManager] updatePrimarySecurity history skipped', e)
        }
        updatePrimarySecurityAsync(security)(store.dispatch)
    }

    async addComparableSecurity(key: string) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state)
        const chart = this.getChart(activeChartId);
        if (!chart) return;
        const currentComparableSecurities = selectChartComparableSecurities(activeChartId)(state);
        const security = await getSecurityAsync(key);
        if (!security) return
        // Route through `chart.updateComparableSecurities` so its built-in `historyManager.push`
        // for `ADD_COMPARE_SECURITY_INDICATOR` fires (mirrors web flow). Calling Redux directly
        // bypassed history; native-issued compare adds were not undoable.
        chart.updateComparableSecurities([...currentComparableSecurities, security], false)
    }
    updateIndicatorInputValues(seriesGroupId: string, inputValues: any) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state);
        const chart = this.getChart(activeChartId);
        if (!chart) return;

        const seriesGroups = chart.seriesGroupRef || {};

        if (!seriesGroups[seriesGroupId]) {
            console.error("Invalid seriesGroupId:", seriesGroupId);
            return;
        }

        let parsedValues: Record<string, any>;
        try {
            parsedValues = JSON.parse(inputValues);
        } catch (err) {
            console.error("Invalid inputValues JSON:", inputValues, err);
            return;
        }

        chart.updateSeriesGroupInputs(seriesGroupId, parsedValues, true);
    }

    updateIndicatorOutputValues(seriesGroupId: string, outputValues: any) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state);
        const chart = this.getChart(activeChartId);
        if (!chart) return;

        const seriesGroups = chart.seriesGroupRef || {};
        Object.entries(seriesGroups).forEach(([groupId, group]) => {
            console.log(groupId);
            console.log( Object.keys(group.series));
        });
        if (!seriesGroups[seriesGroupId]) {
            console.error("Invalid seriesGroupId:", seriesGroupId);
            return;
        }

        let parsedValues: Record<string, any>;
        try {
            parsedValues = JSON.parse(outputValues);
        } catch (err) {
            console.error("Invalid inputValues JSON:", outputValues, err);
            return;
        }
        console.log("updateSeriesGroupOutputs payload:", {
            seriesGroupId,
            parsedValues
        });

        chart.updateSeriesGroupOutputs(seriesGroupId, parsedValues);
    }

    async getChartAlertSnapshot() {
        try {
            const state = store.getState();
            const activeChartId = this.getActiveChartId()
            const chart = this.getChart(activeChartId)
            const chartPanes = selectChartPanes(activeChartId)(state);
            const chartPanesConfig = selectChartPanesConfig(activeChartId)(state);
            const chartSeriesGroup = selectChartSeriesGroups(activeChartId)(state);
            const chartSeries = selectChartSeries(activeChartId)(state);
            console.warn("[ChartManager] state", state)

            if (!chart) {
                console.warn('[ChartManager] No chart found for activeChartId:', activeChartId)
                return null
            }
            const snapshot = chart.getChartSnapshot(0) || {}
            const primaryData = chart.getRenderedPrimaryData()
            const [candle] = (primaryData || []).slice(-1)

            const securityList = (await Promise.all(
                chart.securities.map((s) => getSecurityAsync(getSecurityKey(s.exchange, s.code)))
            )).filter((s): s is Security => s != null)
            const timeBuckets = selectTimeBuckets(state)
            const chartSnapshot: ChartAlertSnapshot = {
                securityList,
                comparableSecurities: chart.comparableSecurities ?? [],
                availableTimeBuckets: timeBuckets.map((tb) => convertToTimeBucket(tb)),
                chartType: chart.chartType,
                data: {
                    time: candle?.time ?? 0,
                    open: candle?.open ?? 0,
                    high: candle?.high ?? 0,
                    low: candle?.low ?? 0,
                    close: candle?.close ?? 0,
                    customValues: {
                        volume: candle?.volume ?? 0
                    }
                },
                timeBucket: convertToTimeBucket(chart.timeBucket),
                expressionMode: chart.expression && chart.expression.length ? true : false,
                expression: chart.expression || '',
                indicators: []
            }

            chartPanes.forEach((pid) => {
                const paneConfig = chartPanesConfig[pid]
                if (!paneConfig) return

                paneConfig.seriesGroups.forEach((sgid) => {
                    const grp = chartSeriesGroup[sgid]
                    const indicatorGroup = chart.seriesGroupRef[sgid]
                    if (!grp || !indicatorGroup || ![SeriesGroupCategory.SERIES_INDICATOR, SeriesGroupCategory.SERIES_VOLUME].includes(grp.category)) return

                    const { indicator } = indicatorGroup as unknown as IndicatorSeriesGroup
                    const { code, name, inputs, outputs, shortName } = indicator
                    const values: Record<string, SingleValueData> = {}
                    grp.series.forEach((sid) => {
                        const grpSnapshot = snapshot[sgid] || {}
                        const srs = chartSeries[sid]
                        const data = grpSnapshot[sid]
                        if (!data || !data.length || !srs) return
                        const [c] = data.slice(-1)

                        if (c) {
                            values[srs.name] = c as SingleValueData<Time>
                        }
                    })
                    chartSnapshot.indicators.push({
                        id: grp.id,
                        code,
                        shortName,
                        displayName: getIndicatorDisplayName(indicator, grp.inputValues, (value) =>
                            chart.getIndicatorOutputSourceLabel(value)
                        ),
                        name,
                        config: grp.inputValues,
                        inputs,
                        outputs,
                        outputValues: values
                    })
                })
            })

            console.log('[ChartManager] getChartAlertSnapshot result:', chartSnapshot)
            return chartSnapshot
        } catch (e) {
            console.error('[ChartManager] Error in getChartAlertSnapshot:', e)
            return null
        }
    }

    selectDrawingTool(drawingType: any, toolOptions: any) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state)
        const chart = this.getChart(activeChartId)
        if (!chart) return;
        if(drawingType === "magnet") chart.drawingManager.setMagnetMode(!chart.drawingManager.magnetMode)
        else chart.drawingManager.addDrawing(drawingType, DrawingDisplayType.DRAWING, toolOptions, {})
    }

    publishSocketMessage(message: string) {
        var binaryString = atob(message);
        var bytes = new Uint8Array(binaryString.length);
        for (var i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const candle = WebSocketResponseMessage_CandleSubscriptionResponse.decode(new Uint8Array(bytes));
        this._publishToWebsocketManager && this._publishToWebsocketManager(candle);
    }

    async addOrderToChart(transactionType: TransactionType, orderType: OrderType, brokerOrderType: Broker_OrderType, price: number) {
        const state = store.getState();
        const activeChartId = selectActiveChartId(state)
        const chart = this.getChart(activeChartId)
        if (!chart) return;
        const chartSecurity = chart.securities[0]
        const security = await getSecurityAsync(getSecurityKey(chartSecurity?.exchange, chartSecurity?.code))
        if (!security) return;
        const chartDrawing = chart?.drawingManager?.getDrawing(chart?.ordersOnChartDrawingId) as OrdersOnChartTool
        if (!chartDrawing) return;
        const newPrice = Number(chartDrawing.roundPriceToTickSize(Number(price)/(security.priceMultiplier??100))) * (security?.priceMultiplier??100)
        chartDrawing.setPendingOpenOrder({
            security,
            transactionType: transactionType,
            orderType,
            brokerOrderType,
            quantity: 1 * (security?.lotSize??1),
            isBasket: false,
            orderDate: Date.now(),
            product: Product.INTRADAY,
            price: orderType===OrderType.NORMAL ? newPrice : 0,
            triggerPrice: orderType===OrderType.TRIGGER ? Math.round(newPrice) : 0,
            brokerVariety: Broker_Variety.REGULAR,
            validity: Validity.DAY,
            clientOrderId: uuid(),
            immediateExecutePaperOrder: false
        })
    }

    updateDrawingSettings(seriesGroupId: string, drawingId: string, settingsJson: string) {
        try {
            const state = store.getState();
            const activeChartId = selectActiveChartId(state);
            const chart = this.getChart(activeChartId);
            if (!chart) return;

            const drawing = chart.drawingManager.getDrawing(drawingId);
            if (!drawing) return;

            const settings = JSON.parse(settingsJson);
            
            Object.entries(settings).forEach(([key, value]) => {
                drawing.updateSettings({ [key]: value });
            });
        } catch (e) {
            console.error("Error updating drawing settings:", e);
        }
    }
}
export default ChartManager